import { Instant, LocalDate, LocalTime, LocalDateTime, DateTimeFormatter, ZoneId, Locale } from './datetime';
import { parseLocalDateTimeN, parseInstantN } from './parsers';
export const formatFileSize = (bytes, unit) => {
    let target = unit;
    const units = {
        'TB': Math.pow(1024, 4),
        'GB': Math.pow(1024, 3),
        'MB': Math.pow(1024, 2),
        'KB': Math.pow(1024, 1),
        'B': Math.pow(1024, 0),
    };
    if (!target) {
        for (const [name, value] of Object.entries(units).toSorted((a, b) => b[1] - a[1])) {
            if (bytes >= value) {
                target = name;
                break;
            }
        }
    }
    return `${(bytes / units[target]).toFixed(2)} ${target}`;
};
export const isImageFile = (filename) => {
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
};
export const getFileTypeIcon = (filename) => {
    var _a;
    const ext = (_a = filename.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    switch (ext) {
        case 'pdf':
            return 'bi-file-earmark-pdf';
        case 'doc':
        case 'docx':
            return 'bi-file-earmark-text';
        case 'xls':
        case 'xlsx':
        case 'csv':
            return 'bi-file-earmark-spreadsheet';
        case 'txt':
            return 'bi-file-text';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'webp':
            return 'bi-image';
        default:
            return 'bi-file-earmark';
    }
};
export const getMimeTypeFromFilename = (filename) => {
    var _a;
    const ext = (_a = filename.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    switch (ext) {
        case 'png':
            return 'image/png';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'gif':
            return 'image/gif';
        case 'webp':
            return 'image/webp';
        case 'mp4':
            return 'video/mp4';
        case 'webm':
            return 'video/webm';
        case 'pdf':
            return 'application/pdf';
        case 'txt':
            return 'text/plain';
        case 'csv':
            return 'text/csv';
        case 'xls':
            return 'application/vnd.ms-excel';
        case 'xlsx':
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case 'doc':
            return 'application/msword';
        case 'docx':
            return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        default:
            return 'application/octet-stream';
    }
};
export const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
};
export const base64ToArrayBuffer = (base64) => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
};
export const formatCurrency = (x) => {
    return new Intl.NumberFormat('en-US', {
        'style': 'currency',
        'currency': x[1],
        'minimumFractionDigits': 2,
        'maximumFractionDigits': 2,
    }).format(x[0]);
};
export const formatDollars = (x) => formatCurrency([x, 'USD']);
export const formatNumber = (x) => {
    return new Intl.NumberFormat('en-US', {}).format(x);
};
export const formatNumberD = (x, digits = 2) => {
    return new Intl.NumberFormat('en-US', {
        'maximumFractionDigits': digits,
    }).format(x);
};
export const calendarFormat = (date, fmt, culture) => {
    const locale = culture || 'en-US';
    const options = fmt === 'P' || fmt === 'PP'
        ? { year: 'numeric', month: 'short', day: 'numeric' }
        : fmt === 'p'
            ? { hour: 'numeric', minute: '2-digit' }
            : { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    return new Intl.DateTimeFormat(locale, options).format(date);
};
export const calendarParse = (value) => new Date(Date.parse(value));
export const calendarStartOfWeek = (date) => {
    const d = new Date(date);
    const diff = d.getDay();
    d.setDate(d.getDate() - diff);
    return d;
};
export const calendarGetDay = (date) => date.getDay();
export const FORMAT_DATE_SHORT = 'M/d/yyyy';
export const FORMAT_DATE_MID = 'MMMM d, yyyy';
export const FORMAT_DATE_LONG = 'EEEE MMMM d, yyyy';
export const FORMAT_TIME = 'h:mm a';
// TODO: ideally would invert to be `formatDate` called by `formatDateN` but can't risk breaking existing `formatDate`
export const formatDateNN = (x, format = FORMAT_DATE_SHORT) => {
    const pattern = DateTimeFormatter.ofPattern(format).withLocale(Locale.US);
    return (x instanceof LocalDate
        ? x.format(pattern)
        : x instanceof Instant
            ? x.atZone(ZoneId.SYSTEM).format(pattern)
            : (console.error('formatDate - fallthrough', x), '#ERROR#'));
};
export const formatDate = (x, format = FORMAT_DATE_SHORT) => x === null ? '' : formatDateNN(x, format);
export const formatDateLong = (x) => formatDate(x, FORMAT_DATE_LONG);
export const formatTime = (x, format = FORMAT_TIME) => {
    const pattern = DateTimeFormatter.ofPattern(format).withLocale(Locale.US);
    return (x === null
        ? ''
        : x instanceof LocalTime
            ? x.format(pattern)
            : x instanceof Instant
                ? x.atZone(ZoneId.SYSTEM).format(pattern)
                : (console.error('formatTime - fallthrough', x), '#ERROR#'));
};
export const formatDateTime = (x, format = `${FORMAT_DATE_SHORT} ${FORMAT_TIME}`) => {
    const pattern = DateTimeFormatter.ofPattern(format).withLocale(Locale.US);
    return (x === null
        ? ''
        : x instanceof LocalDateTime
            ? x.format(pattern)
            : x instanceof Instant
                ? x.atZone(ZoneId.SYSTEM).format(pattern)
                : (console.error('formatDateTime - fallthrough', x), '#ERROR#'));
};
export const formatDatetimeLong = (x) => formatDateTime(x, `${FORMAT_DATE_LONG} ${FORMAT_TIME}`);
export const formatPhone = (raw) => {
    const _formatPhone = (x) => {
        switch (x.length) {
            case 11:
                return x.startsWith('1') ? _formatPhone(x.substring(1)) : x;
            case 10:
                return `(${x.substring(0, 3)}) ${x.substring(3, 6)}-${x.substring(6)}`;
            default:
                return raw;
        }
    };
    return _formatPhone(raw.replaceAll(/\D+/g, ''));
};
export const maskPhone = (raw) => {
    const digits = raw.replaceAll(/\D+/g, '');
    const normalized = digits.length === 11 && digits.startsWith('1') ? digits.substring(1) : digits;
    const d = normalized.substring(0, 10);
    if (d.length === 0)
        return '';
    if (d.length <= 3)
        return `(${d}`;
    if (d.length <= 6)
        return `(${d.substring(0, 3)}) ${d.substring(3)}`;
    return `(${d.substring(0, 3)}) ${d.substring(3, 6)}-${d.substring(6)}`;
};
const pluralRules = [
    [/[sc]h$/i, '$&es'],
    [/[^aeiou]y$/i, 'ies'],
    [/[xz]$/i, '$&es'],
    [/s$/i, '$&'],
];
export const dangerousPluralHelper = (word) => {
    for (const [pattern, replacement] of pluralRules)
        if (pattern.test(word))
            return word.replace(pattern, replacement);
    return word + 's';
};
// String-in → string-out display helpers (timezone-aware)
export const displayDate = (x) => {
    const parsed = parseLocalDateTimeN(x);
    return parsed ? formatDate(parsed.toLocalDate()) : '';
};
export const displayDateLong = (x) => {
    const parsed = parseLocalDateTimeN(x);
    return parsed ? formatDateLong(parsed.toLocalDate()) : '';
};
export const displayTime = (x) => formatTime(parseInstantN(x));
export const displayDateTime = (x) => formatDateTime(parseLocalDateTimeN(x));
export const formatRelativeTime = (x) => {
    if (!x)
        return '';
    const instant = parseInstantN(x.endsWith('Z') ? x : x + 'Z');
    if (!instant)
        return '';
    const now = Instant.now();
    const diffMs = now.toEpochMilli() - instant.toEpochMilli();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 60)
        return `${Math.max(diffMins, 0)}m ago`;
    if (diffHours < 24)
        return `${diffHours}h ago`;
    if (diffDays < 7)
        return `${diffDays}d ago`;
    return displayDate(x);
};
export const upperCasedWords = (s) => s.toLowerCase().replace(/\b[a-z]/g, (l) => l.toUpperCase());
